import { useEffect } from 'react';
import { usePreferences } from '../context/PreferencesContext';
import { findReleaseItem, type Release, type ReleaseItem } from './releases';

/**
 * A badge stays this long after the user first had it in front of them. Nothing depends
 * on the release date: production receives several releases at once, long after they
 * were written, and a feature nobody has discovered yet must not expire.
 */
export const NEW_BADGE_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;
const SEEN_KEY = 'new-seen';

/** `new-seen` preference: feature key → ISO date of the first time the user saw its badge. */
type SeenMap = Record<string, string>;

/** New until first sight, then for `NEW_BADGE_DAYS`. */
export function isNewFor(firstSeen: string | undefined, now = new Date()): boolean {
  if (!firstSeen) return true;
  return now.getTime() - new Date(firstSeen).getTime() < NEW_BADGE_DAYS * DAY_MS;
}

/**
 * Whether the feature behind `key` (an entry of `releases.ts`) should be flagged as new for
 * the current user, and the release entry to explain it. Rendering the hook records the
 * first sight in the user's server-side preferences, so the badge lifetime follows them
 * from one machine to another.
 */
export function useNewFeature(key: string): { isNew: boolean; item: ReleaseItem | null; release: Release | null } {
  const { ready, get, set } = usePreferences();
  const found = findReleaseItem(key);
  const seen = (ready ? get<SeenMap>(SEEN_KEY) : null) ?? {};
  const isNew = Boolean(found) && ready && isNewFor(seen[key]);

  useEffect(() => {
    if (!isNew) return;
    const current = get<SeenMap>(SEEN_KEY) ?? {};
    if (!current[key]) set(SEEN_KEY, { ...current, [key]: new Date().toISOString().slice(0, 10) });
  }, [isNew, key, get, set]);

  return { isNew, item: found?.item ?? null, release: found?.release ?? null };
}
